<template>
  <el-dialog
      :title="$t('text.batchChannelConfig')"
      :visible.sync="visible"
      width="860px"
      :close-on-click-modal="false"
      @close="onClose"
  >
    <ol class="excel-tip">
      <li>{{ $t('message.channelConfigExcelStep1') }}</li>
      <li>{{ $t('message.channelConfigExcelStep2') }}</li>
      <li>{{ $t('message.channelConfigExcelStep3') }}</li>
    </ol>
    <el-button type="primary" size="small" :loading="exporting" @click="downloadCurrent">
      {{ $t('text.downloadCurrentChannelConfig') }}
    </el-button>
    <el-upload
        class="excel-upload"
        ref="upload"
        drag
        action=""
        :auto-upload="false"
        :limit="1"
        :file-list="fileList"
        :on-change="onFileChange"
        :on-remove="onFileRemove"
        :on-exceed="replaceFile"
        accept=".xlsx"
    >
      <i class="el-icon-upload"/>
      <div class="el-upload__text">{{ $t('message.dropOrClickUpload') }}</div>
      <div slot="tip" class="el-upload__tip">{{ $t('message.onlyExcelFiles') }}</div>
    </el-upload>

    <p v-if="preview" class="excel-summary">
      {{ $t('message.channelConfigExcelSummary', {blank: preview.blankCellCount || 0, unchanged: preview.unchangedRowCount || 0}) }}
    </p>

    <div v-if="changeGroups.length" class="excel-groups">
      <section v-for="group in changeGroups" :key="group.key" class="excel-group">
        <div class="excel-group-title">#{{ group.rowNum }} {{ group.merName }}</div>
        <el-table :data="group.changes" size="mini" border>
          <el-table-column prop="column" :label="$t('text.channelConfigField')" min-width="140" align="center"/>
          <el-table-column prop="oldValue" :label="$t('text.originalChannels')" min-width="180"/>
          <el-table-column :label="$t('text.newChannels')" min-width="180">
            <template slot-scope="scope">
              <span v-if="scope.row.cleared">{{ $t('text.cleared') }}</span>
              <span v-else>{{ scope.row.newValue }}</span>
            </template>
          </el-table-column>
        </el-table>
      </section>
    </div>

    <el-alert
        v-if="errors.length"
        :title="$t('message.channelConfigErrorCount', {count: errors.length})"
        type="error"
        :closable="false"
        show-icon
        style="margin-top: 8px"
    >
      <ul class="excel-error-list">
        <li v-for="(error, index) in errors" :key="index">{{ error }}</li>
      </ul>
    </el-alert>

    <div slot="footer" class="dialog-footer">
      <el-button @click="visible = false">{{ $t('text.cancel') }}</el-button>
      <el-button type="primary" :loading="previewing" :disabled="!selectedFile" @click="previewFile">
        {{ $t('text.previewChannelConfig') }}
      </el-button>
      <el-button type="primary" :loading="importing" :disabled="!canImport" @click="confirmImport">
        {{ $t('text.confirmImportChannelConfig') }}
      </el-button>
    </div>
  </el-dialog>
</template>

<script>
export default {
  name: "PkChannelConfigExcelDialog",
  props: {
    show: {
      type: Boolean,
      default: false
    },
    query: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      exporting: false,
      previewing: false,
      importing: false,
      fileList: [],
      selectedFile: null,
      preview: null
    };
  },
  computed: {
    visible: {
      get() {
        return this.show;
      },
      set(val) {
        this.$emit("update:show", val);
      }
    },
    changes() {
      return (this.preview && this.preview.changes) || [];
    },
    changeGroups() {
      const groups = [];
      const indexByKey = {};
      this.changes.forEach((change) => {
        const key = String(change.rowNum) + ":" + (change.merName || "");
        if (indexByKey[key] === undefined) {
          indexByKey[key] = groups.length;
          groups.push({
            key: key,
            rowNum: change.rowNum,
            merName: change.merName,
            changes: []
          });
        }
        groups[indexByKey[key]].changes.push(change);
      });
      return groups;
    },
    errors() {
      return (this.preview && this.preview.errors) || [];
    },
    canImport() {
      return this.selectedFile && this.preview && this.errors.length === 0 && this.changes.length > 0 && !this.previewing;
    }
  },
  methods: {
    onClose() {
      this.fileList = [];
      this.selectedFile = null;
      this.preview = null;
      this.exporting = false;
      this.previewing = false;
      this.importing = false;
    },
    onFileChange(file, fileList) {
      this.fileList = fileList.slice(-1);
      this.selectedFile = file && file.raw ? file.raw : null;
      this.preview = null;
    },
    onFileRemove() {
      this.fileList = [];
      this.selectedFile = null;
      this.preview = null;
    },
    replaceFile(files) {
      const raw = files && files[0];
      if (!raw || !this.$refs.upload) {
        return;
      }
      this.$refs.upload.clearFiles();
      this.$refs.upload.handleStart(raw);
    },
    downloadCurrent() {
      this.exporting = true;
      this.$axios
          .get("/routeConfigInfo/channelConfigExport", {
            params: {...this.query},
            responseType: "blob",
            timeout: 120 * 1000
          })
          .then((res) => {
            const payload = res && res.headers ? res.data : res;
            if (payload && payload.code && payload.code !== 200) {
              this.$notify.error({
                title: this.$t("text.prompt"),
                message: payload.msg
              });
              return;
            }
            if (payload instanceof Blob && payload.type && payload.type.indexOf("application/json") >= 0) {
              payload.text().then(JSON.parse).then((body) => {
                this.$notify.error({
                  title: this.$t("text.prompt"),
                  message: body.msg || this.$t("message.exportFailed")
                });
              });
              return;
            }
            const url = window.URL.createObjectURL(new Blob([payload]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", "route-channel-config.xlsx");
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
          })
          .catch(() => {
            this.$notify.error({
              title: this.$t("text.prompt"),
              message: this.$t("message.exportFailed")
            });
          })
          .finally(() => {
            this.exporting = false;
          });
    },
    formData() {
      const formData = new FormData();
      formData.append("file", this.selectedFile);
      return formData;
    },
    postFile(url) {
      return this.$axios.post(url, this.formData(), {
        transformRequest: [
          function (data, headers) {
            delete headers.post["Content-Type"];
            return data;
          }
        ]
      });
    },
    previewFile() {
      if (!this.selectedFile) {
        this.$message.error(this.$t("message.pleaseSelectFile"));
        return;
      }
      this.previewing = true;
      this.postFile("/routeConfigInfo/channelConfigImportPreview")
          .then(res => {
            this.preview = (res && res.data) || {errors: [res && res.msg], changes: []};
            if (!this.errors.length && !this.changes.length) {
              this.$message.info(this.$t("message.channelConfigNoChange"));
            } else if (res.code !== 200) {
              this.$notify.error({
                title: this.$t("text.prompt"),
                message: res.msg
              });
            }
          })
          .catch(() => {
            this.$notify.error({
              title: this.$t("text.prompt"),
              message: this.$t("message.uploadFailed")
            });
          })
          .finally(() => {
            this.previewing = false;
          });
    },
    confirmImport() {
      if (!this.canImport) {
        return;
      }
      this.importing = true;
      this.postFile("/routeConfigInfo/channelConfigImport")
          .then(res => {
            if (res.code === 200) {
              this.$notify({
                title: this.$t("text.prompt"),
                message: res.msg,
                type: "success"
              });
              this.$emit("imported");
              this.visible = false;
              return;
            }
            this.preview = (res && res.data) || this.preview;
            this.$notify.error({
              title: this.$t("text.prompt"),
              message: res.msg
            });
          })
          .catch(() => {
            this.$notify.error({
              title: this.$t("text.prompt"),
              message: this.$t("message.uploadFailed")
            });
          })
          .finally(() => {
            this.importing = false;
          });
    }
  }
};
</script>

<style scoped>
.excel-tip {
  margin: 0 0 12px;
  padding-left: 18px;
  color: #606266;
  font-size: 13px;
  line-height: 1.7;
}

.excel-upload {
  margin-top: 12px;
}

.excel-summary {
  margin: 12px 0 0;
  color: #606266;
  font-size: 13px;
}

.excel-groups {
  margin-top: 12px;
  max-height: 320px;
  overflow: auto;
}

.excel-group + .excel-group {
  margin-top: 12px;
}

.excel-group-title {
  padding: 6px 10px;
  background: #f5f7fa;
  border: 1px solid #ebeef5;
  border-bottom: none;
  font-weight: 600;
  color: #303133;
}

.excel-error-list {
  margin: 4px 0 0;
  padding-left: 18px;
  max-height: 180px;
  overflow: auto;
  line-height: 1.6;
}
</style>
